"use client";

import { useMemo } from "react";
import { type StreakSummaryResponse, AUTOMATIC_STREAK_STATUS } from "@cede/types";
import { cn } from "@/lib/utils";
import { getStatusConfig, isStreakActive, formatStreakDateRelative } from "@/utils/automatic-streak";
import { CheckCircle, Clock, Pause, Ban, AlertTriangle, Loader2, ChevronRight, Calendar } from "lucide-react";

interface AutomaticStreakCardProps {
  streak: StreakSummaryResponse;
  onClick?: () => void;
  className?: string;
  /** Current epoch ID - used to determine if this streak is for a future epoch */
  currentEpochId?: number;
  /** Hide the "Scheduled for Next Epoch" banner inside the card (use when already shown in section header) */
  hideNextEpochBanner?: boolean;
}

const STATUS_ICONS: Record<string, React.ReactNode> = {
  [AUTOMATIC_STREAK_STATUS.PENDING]: <Clock className="w-4 h-4" />,
  [AUTOMATIC_STREAK_STATUS.IN_PROGRESS]: <Loader2 className="w-4 h-4 animate-spin" />,
  [AUTOMATIC_STREAK_STATUS.COMPLETED]: <CheckCircle className="w-4 h-4" />,
  [AUTOMATIC_STREAK_STATUS.STOPPED]: <Pause className="w-4 h-4" />,
  [AUTOMATIC_STREAK_STATUS.CANCELLED]: <Ban className="w-4 h-4" />,
};

const DEFAULT_ICON = <AlertTriangle className="w-4 h-4" />;

export const AutomaticStreakCard = ({
  streak,
  onClick,
  className,
  currentEpochId,
  hideNextEpochBanner = false,
}: AutomaticStreakCardProps) => {
  const statusConfig = getStatusConfig(streak.status);
  const statusIcon = STATUS_ICONS[streak.status] ?? DEFAULT_ICON;
  const isActive = isStreakActive(streak.status);
  const isNextEpoch = currentEpochId !== undefined && streak.epochId > currentEpochId;
  const showNextEpochBanner = isNextEpoch && !hideNextEpochBanner;

  const progress = useMemo(() => {
    if (streak.totalTrades === 0) return 0;
    return (streak.completedTrades / streak.totalTrades) * 100;
  }, [streak.completedTrades, streak.totalTrades]);

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "w-full p-4 rounded-lg border bg-cede-bg-component",
        "hover:bg-cede-bg-component-hover",
        "transition-all duration-200 text-left",
        onClick && "cursor-pointer",
        // Different border styles based on state
        isNextEpoch
          ? "border-cede-primary/40 border-dashed"
          : isActive
            ? "border-cede-primary/30"
            : "border-cede-border-container",
        className,
      )}
    >
      {/* Next Epoch Banner - prominent at top (hidden when section header already shows this) */}
      {showNextEpochBanner && (
        <div className="flex items-center gap-2 mb-3 pb-3 border-b border-cede-border-container">
          <Calendar className="w-4 h-4 text-cede-primary" />
          <span className="text-xs font-medium text-cede-primary">Scheduled for Next Epoch</span>
        </div>
      )}

      <div className="flex items-start gap-3">
        {/* Left: Status icon */}
        <div className={cn("p-2 rounded-lg shrink-0", statusConfig.bgColor, statusConfig.color)}>{statusIcon}</div>

        {/* Middle: Main content */}
        <div className="flex-1 min-w-0">
          {/* Row 1: Pair symbol and status */}
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-semibold text-cede-text-primary text-base">{streak.pairSymbol}</h3>
            <span
              className={cn("px-2 py-0.5 rounded-full text-xs font-medium", statusConfig.bgColor, statusConfig.color)}
            >
              {statusConfig.label}
            </span>
          </div>

          {/* Row 2: Progress bar - full width for better visibility */}
          <div className="mb-2">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-cede-text-secondary">
                {streak.completedTrades}/{streak.totalTrades} cycles
              </span>
              <span className="text-xs font-medium text-cede-text-primary">{Math.round(progress)}%</span>
            </div>
            <div className="w-full h-2 rounded-full bg-cede-bg-primary overflow-hidden">
              <div
                className={cn(
                  "h-full rounded-full transition-all duration-300",
                  streak.status === AUTOMATIC_STREAK_STATUS.COMPLETED
                    ? "bg-cede-success"
                    : streak.status === AUTOMATIC_STREAK_STATUS.STOPPED
                      ? "bg-cede-warning"
                      : "bg-cede-primary",
                )}
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          {/* Row 3: Volume and time info */}
          <div className="flex items-center justify-between text-xs text-cede-text-secondary">
            <span className="font-medium">${parseFloat(streak.totalVolumeTraded).toFixed(2)} traded</span>
            <span>
              {streak.finishedAt
                ? formatStreakDateRelative(streak.finishedAt)
                : streak.actualStartTime
                  ? `Started ${formatStreakDateRelative(streak.actualStartTime)}`
                  : formatStreakDateRelative(streak.scheduledStartTime)}
            </span>
          </div>
        </div>

        {/* Right: Chevron */}
        {onClick && <ChevronRight className="w-5 h-5 text-cede-text-tertiary shrink-0 mt-1" />}
      </div>

      {/* Active indicator - at bottom */}
      {isActive && !isNextEpoch && (
        <div className="flex items-center gap-2 mt-3 pt-3 border-t border-cede-border-container">
          <div className={cn("w-2 h-2 rounded-full animate-pulse", statusConfig.dotColor)} />
          <span className="text-xs text-cede-text-secondary">
            {streak.status === AUTOMATIC_STREAK_STATUS.IN_PROGRESS ? "Executing trades..." : "Waiting to start"}
          </span>
        </div>
      )}
    </button>
  );
};
